import { Bundle } from '@ali/visualengine';
import React from 'react';
// import { createSlot } from '@ali/vu-slot-property';
import Icon from './logo.tsx';
import { createMessageSetters } from '@/common/util/message';
import {
  createButtonListSetters,
  getItem,
} from '@/common/manager/button';
import { ButtonPosition } from '@/common/manager/position/button-position';
import {
  getCnSelectSetterSnippet,
  getDataOriginSetterSnippet,
  getJSXTemplate,
  getMixedSetterSnippet,
  getNodeIdSetterSnippet,
  getObjectSetterSnippet,
  getRequestConfigSetterSnippet,
  getStaticDataSourceSnippet,
  getDataSetterSnippet,
} from '@/common/manager/setter-snippet';
import { getJSExpressionPrototype } from '@/common/manager/common-style';
import {
  dataOriginRequest,
  dataOriginStatic,
  statusList,
} from '@/common/util/const';
import { createEventSetters } from '@/common/manager/event';
import { createDataSourceSetters } from '@/lowcode/cn-form-alc/prototype/form-item';
import { handleI18nLabel } from '@/common/util/util';
import { __dataSource__ } from '@/common/util/expr-const';
import { uuid } from '@/common/util/util';
import TipSetter from '@/common/setter/tip-setter';
import { DisplayPosition } from '@/common/manager/position/display-position';

const CnListBundle = Bundle.createPrototype({
  title: '列表（CnList）',
  componentName: 'CnList',
  category: '数据展示',
  icon: Icon,
  docUrl:
    'https://cone.cainiao-inc.com/market/awesome?business=internationality&id=3313',
  isContainer: false,
  canHovering: true,
  canSelecting: true,
  canDragging: true,
  enableCopy: true,
  snippets: [
    {
      screenshot:
        'https://img.alicdn.com/imgextra/i2/O1CN011CqPrA1lUWgbOoKif_!!6000000004822-0-tps-1884-628.jpg',
      label: 'CnList',
      schema: {
        componentName: 'CnList',
        props: {},
      },
    },
  ],
  configure: [
    {
      name: 'isCnList',
      title: 'isCnList',
      initialValue: true,
      display: 'none',
    },
    {
      name: '_context',
      title: '上下文',
      initialValue: {
        type: 'JSExpression',
        value: 'this',
      },
      display: 'none',
    },
    {
      name: 'header',
      title: '列表标题',
      display: 'inline',
      initialValue: '列表',
      setter: 'CnI18nSetter',
    },
    ...getDataSetterSnippet(),
    {
      title: '样式',
      type: 'group',
      display: 'accordion',
      collapsed: false,
      extraProps: {
        defaultCollapsed: false,
      },
      items: [
        {
          name: 'itemLayout',
          display: 'inline',
          title: '布局',
          defaultValue: 'vertical',
          setter: {
            componentName: 'RadioGroupSetter',
            props: {
              options: [
                { value: 'horizontal', title: '水平' },
                { value: 'vertical', title: '竖直' },
              ],
            },
          },
        },
        {
          name: 'size',
          display: 'inline',
          title: '尺寸',
          defaultValue: 'medium',
          setter: {
            componentName: 'RadioGroupSetter',
            props: {
              options: [
                { title: '大', value: 'medium' },
                { title: '小', value: 'small' },
              ],
            },
          },
        },
        {
          name: 'horizontalConfig',
          display: 'plain',
          title: '水平配置',
          setter: getObjectSetterSnippet({
            items: [
              {
                name: 'size',
                display: 'inline',
                title: '布局',
                setter: {
                  componentName: 'RadioGroupSetter',
                  props: {
                    options: [
                      { title: '极紧凑', value: 'xs' },
                      { title: '紧凑', value: 'small' },
                      { title: '正常', value: 'medium' },
                      { title: '宽松', value: 'large' },
                    ],
                  },
                },
              },
            ],
          }),
          disabled() {
            return this.getProps().getPropValue('itemLayout') !== 'horizontal';
          },
        },
        {
          name: 'selectMode',
          display: 'inline',
          title: '选择模式',
          setter: {
            componentName: 'RadioGroupSetter',
            props: {
              options: [
                { value: 'multiple', title: '多选' },
                { value: 'single', title: '单选' },
                { title: '不设置' },
              ],
            },
          },
        },
        {
          name: 'shape',
          display: 'inline',
          title: '列表项表现形式',
          defaultValue: 'default',
          setter: {
            componentName: 'RadioGroupSetter',
            props: {
              options: [
                { title: '卡片', value: 'card' },
                { title: '普通', value: 'default' },
              ],
            },
          },
        },
        {
          name: 'itemDescriptionRender',
          display: 'inline',
          title: '描述自定义渲染',
          setter: getMixedSetterSnippet({
            setters: [
              <TipSetter tip={'请点击右侧切换按钮写JS表达式'} />,
              getJSXTemplate({
                position: DisplayPosition.listItemDescriptionRender,
              }),
            ],
          }),
        },
        {
          name: 'itemContentRender',
          display: 'inline',
          title: '内容自定义渲染',
          setter: getMixedSetterSnippet({
            setters: [
              <TipSetter tip={'请点击右侧切换按钮写JS表达式'} />,
              getJSXTemplate({
                position: DisplayPosition.listItemContentRender,
              }),
            ],
          }),
        },
      ],
    },
    {
      name: 'buttons',
      title: '右侧按钮栏',
      display: 'accordion',
      // collapsed:false,
      initialValue: [],
      // supportVariable: true,
      setter: createButtonListSetters({
        position: ButtonPosition.listItemRightButton,
        extraInitialValues: {
          size: 'small',
        },
      }),
    },
    ...createEventSetters({
      position: ButtonPosition.listItemEvent,
      initialValue: {},
      title: '配置列表项点击的回调',
    }),
    {
      title: '高级配置',
      type: 'group',
      collapsed: true,
      display: 'accordion',
      items: [
        getNodeIdSetterSnippet(),
        {
          name: 'handleProps',
          title: '手动设置组件props',
          display: 'inline',
          setter: getJSExpressionPrototype({
            type: 'props',
          }),
        },
        ...createDataSourceSetters(),
      ],
    },
  ],
});

export default CnListBundle;
